/**
 * Service for detecting user's IP address
 */
class IpService {
	static cachedIp = null;
	static ipFetchPromise = null;

	/**
	 * Get user's IP address from multiple sources
	 */
	static async getUserIp() {
		// Return cached IP if available
		if (this.cachedIp) {
			return this.cachedIp;
		}

		// Return existing promise if IP fetch is in progress
		if (this.ipFetchPromise) {
			return this.ipFetchPromise;
		}

		// Start IP detection
		this.ipFetchPromise = this._detectIp();

		try {
			const ip = await this.ipFetchPromise;
			this.cachedIp = ip;
			return ip;
		} catch (error) {
			console.warn("Failed to detect user IP:", error);
			return null;
		} finally {
			this.ipFetchPromise = null;
		}
	}

	/**
	 * Detect IP using multiple fallback services
	 */
	static async _detectIp() {
		const ipSources = [
			// Primary: ipapi.co (free, no key required)
			{
				url: "https://ipapi.co/json/",
				extract: (data) => data.ip,
			},
			// Fallback 1: ipify
			{
				url: "https://api.ipify.org?format=json",
				extract: (data) => data.ip,
			},
			// Fallback 2: ip-api.com
			{
				url: "http://ip-api.com/json/",
				extract: (data) => data.query,
			},
			// Fallback 3: httpbin (last resort)
			{
				url: "https://httpbin.org/ip",
				extract: (data) => data.origin,
			},
		];

		for (const source of ipSources) {
			try {
				const response = await fetch(source.url, {
					method: "GET",
					timeout: 5000, // 5 second timeout
				});

				if (response.ok) {
					const data = await response.json();
					const ip = source.extract(data);

					if (ip && this._isValidIp(ip)) {
						return ip;
					}
				}
			} catch (error) {
				// console.warn(`Failed to get IP from ${source.url}:`, error.message);
				continue;
			}
		}

		throw new Error("Could not detect user IP from any source");
	}

	/**
	 * Basic IP validation
	 */
	static _isValidIp(ip) {
		if (!ip || typeof ip !== "string") return false;

		// Remove any extra info (some services return "ip, proxy_ip")
		const cleanIp = ip.split(",")[0].trim();

		// Basic IPv4 regex
		const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
		// Basic IPv6 regex (simplified)
		const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;

		return ipv4Regex.test(cleanIp) || ipv6Regex.test(cleanIp);
	}

	/**
	 * Clear cached IP (useful for testing or when IP might change)
	 */
	static clearCache() {
		this.cachedIp = null;
		this.ipFetchPromise = null;
	}

	/**
	 * Get cached IP without making new request
	 */
	static getCachedIp() {
		return this.cachedIp;
	}
}

export default IpService;
